import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, Users, FileText, MapPin } from 'lucide-react';
import { format, addHours } from 'date-fns';

const AssignEventModal = ({ isOpen, staffIds = [], staff = [], events = [], onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const [formData, setFormData] = useState({
    eventId: '',
    roleDescription: '',
    shiftStart: '',
    shiftEnd: '',
    notes: ''
  });

  // Get selected staff details
  const selectedStaff = staff?.filter(member => staffIds?.includes(member?.id)) || [];
  
  // Get upcoming events only
  const upcomingEvents = events?.filter(event => 
    new Date(event?.event_date) > new Date()
  ) || [];

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle outside click
  const handleBackdropClick = (event) => {
    if (modalRef?.current && !modalRef?.current?.contains(event?.target)) {
      onClose();
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-populate shift times when event is selected
      if (field === 'eventId' && value) {
        const selectedEvent = events?.find(event => event?.id === value);
        if (selectedEvent?.event_date) {
          const eventDate = new Date(selectedEvent.event_date);
          const shiftStart = addHours(eventDate, -2); // 2 hours before event
          const shiftEnd = selectedEvent?.end_date 
            ? addHours(new Date(selectedEvent.end_date), 1) // 1 hour after event ends
            : addHours(eventDate, 4); // Default 4 hours after event start
          
          newData.shiftStart = shiftStart?.toISOString()?.slice(0, 16);
          newData.shiftEnd = shiftEnd?.toISOString()?.slice(0, 16);
        }
      }
      
      return newData;
    });
    setError(null);
  };

  // Validate form
  const validateForm = () => {
    if (!formData?.eventId) {
      return 'Please select an event';
    }
    if (!formData?.roleDescription?.trim()) {
      return 'Please enter a role description';
    }
    if (!formData?.shiftStart) {
      return 'Please set shift start time';
    }
    if (!formData?.shiftEnd) {
      return 'Please set shift end time';
    }
    if (new Date(formData.shiftEnd) <= new Date(formData.shiftStart)) {
      return 'Shift end time must be after start time';
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await onSubmit(formData);
      
      if (result?.success) {
        // Reset form
        setFormData({
          eventId: '',
          roleDescription: '',
          shiftStart: '',
          shiftEnd: '',
          notes: ''
        });
        onClose();
      } else {
        setError(result?.error || 'Failed to assign staff to event');
      }
    } catch (err) {
      console.error('Error assigning event:', err);
      setError('Failed to assign staff to event');
    } finally {
      setLoading(false);
    }
  };

  const selectedEvent = events?.find(event => event?.id === formData?.eventId);

  // Calculate shift duration
  const getShiftDuration = () => {
    if (formData?.shiftStart && formData?.shiftEnd) {
      const start = new Date(formData.shiftStart);
      const end = new Date(formData.shiftEnd);
      const durationHours = (end - start) / (1000 * 60 * 60);
      return Math.round(durationHours * 10) / 10; // Round to 1 decimal
    }
    return 0;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e?.stopPropagation()} // Prevent click from bubbling to backdrop
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-blue-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Assign Staff to Event</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Selected Staff */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Selected Staff ({selectedStaff?.length || 0})
                </h3>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedStaff?.map((member) => (
                      <div 
                        key={member?.id} 
                        className="flex items-center p-3 bg-white rounded-lg border border-gray-200"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-blue-600 font-medium text-sm">
                            {member?.user_profiles?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member?.user_profiles?.full_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {member?.staff_role?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Event Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Event *
                </label>
                <select
                  value={formData?.eventId}
                  onChange={(e) => handleChange('eventId', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Choose an event...</option>
                  {upcomingEvents?.map((event) => (
                    <option key={event?.id} value={event?.id}>
                      {event?.title} - {event?.event_date && format(new Date(event?.event_date), 'MMM d, yyyy HH:mm')}
                    </option>
                  ))}
                </select>
                
                {upcomingEvents?.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    No upcoming events available for assignment.
                  </p>
                )}
              </div>

              {/* Event Details */}
              {selectedEvent && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Event Details</h4>
                  <div className="space-y-2 text-sm text-blue-800">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {selectedEvent?.event_date && format(new Date(selectedEvent?.event_date), 'EEEE, MMMM d, yyyy \'at\' HH:mm')}
                    </div>
                    {selectedEvent?.end_date && (
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Ends: {format(new Date(selectedEvent?.end_date), 'HH:mm')}
                      </div>
                    )}
                    {selectedEvent?.location && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {selectedEvent?.location}
                      </div>
                    )}
                    {selectedEvent?.venue_name && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {selectedEvent?.venue_name}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Role Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role Description *
                </label>
                <input
                  type="text"
                  value={formData?.roleDescription}
                  onChange={(e) => handleChange('roleDescription', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Main registration desk, Security entrance, VIP check-in"
                  required
                />
              </div>

              {/* Shift Times */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Shift Schedule</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift Start *
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="datetime-local"
                        value={formData?.shiftStart}
                        onChange={(e) => handleChange('shiftStart', e?.target?.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift End *
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="datetime-local"
                        value={formData?.shiftEnd}
                        onChange={(e) => handleChange('shiftEnd', e?.target?.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {formData?.shiftStart && formData?.shiftEnd && (
                  <div className="mt-2 text-sm text-gray-600">
                    Shift Duration: {getShiftDuration()} hours
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Notes
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => handleChange('notes', e?.target?.value)}
                    rows={3}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Special instructions, responsibilities, or other notes..."
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              Assigning {selectedStaff?.length || 0} staff member{selectedStaff?.length !== 1 ? 's' : ''} to event
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || selectedStaff?.length === 0 || upcomingEvents?.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Assigning...' : 'Assign to Event'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AssignEventModal;